<template>
  <TimelineCard>
    <template #header>
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <h3 class="font-[650] tracking-[-0.01em] text-slate-900 text-[0.95rem] m-0 truncate sm:text-[0.8125rem]">
            Task
          </h3>

          <TimelineTag
            :icon="getStatusIcon(task.isCompleted, isOverdue)"
            :label="getStatusLabel(task.isCompleted, isOverdue)"
            :variant="getStatusVariant(task.isCompleted, isOverdue)"
            clickable
            @click="changeTaskStatus(task.id, !task.isCompleted)"
          />

          <TimelineTag
            icon="mdi:calendar-clock"
            :label="formatDueDate(task.dueDate)"
            :variant="getDueDateVariant(isOverdue, task.dueToday || false, task.isCompleted)"
          />
        </div>
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <Button
          v-if="!task.isCompleted"
          variant="text"
          label="Complete"
          size="small"
          @click="changeTaskStatus(task.id, !task.isCompleted)"
        >
          <template #icon>
            <Icon
              name="mdi:check-circle-outline"
              size="14"
            />
          </template>
        </Button>

        <Button
          v-else
          variant="text"
          label="Reopen"
          size="small"
          class="text-emerald-600"
          @click="changeTaskStatus(task.id, !task.isCompleted)"
        >
          <template #icon>
            <Icon
              name="mdi:restore"
              size="14"
            />
          </template>
        </Button>

        <TaskEdit
          v-model:taskId="task.id"
          :users="users"
          @refresh="emit('refresh')"
        />
        <TaskDelete
          v-model:taskId="task.id"
          @refresh="emit('refresh')"
        />
      </div>
    </template>

    <template #content>
      <div class="mt-3 text-[0.85rem] leading-relaxed text-slate-700 sm:text-xs">
        <p class="m-0 break-words">
          {{ task.description }}
        </p>
      </div>

      <div
        v-if="task?.attachments?.length > 0"
        class="mt-3 pt-3 border-t border-slate-100"
      >
        <div class="flex items-center gap-2 flex-wrap">
          <span class="inline-flex items-center gap-[0.35rem] text-[0.72rem] font-bold text-slate-500 px-2 py-1 rounded-full bg-slate-50/90 border border-slate-200/85">
            <Icon
              name="mdi:paperclip"
              size="14"
            />
            Attachments
          </span>

          <a
            v-for="(item, index) in task.attachments"
            :key="index"
            :href="item.publicUrl"
            target="_blank"
            class="inline-flex items-center gap-[0.35rem] px-[0.55rem] py-[0.35rem] rounded-full text-[0.75rem] text-slate-700 no-underline bg-slate-50/90 border border-slate-200/90 transition-all duration-150 ease-in-out hover:-translate-y-0.5 hover:bg-slate-100 hover:border-slate-300"
          >
            <Icon
              name="mdi:file-outline"
              size="14"
            />
            <span class="truncate max-w-[180px]">{{ item.name }}</span>
          </a>
        </div>
      </div>
    </template>

    <template #footer>
      <TimelineFooter
        :author-name="task?.UserWorkSpace?.name"
        :contact="task?.Contact ? {
          id: task.Contact.id,
          firstName: task.Contact.firstName,
          lastName: task.Contact.lastName,
        } : undefined"
        :business="task?.Business ? {
          id: (task as any).businessId || '',
          name: task.Business.name,
        } : undefined"
        :created-at="task.createdAt"
      />
    </template>
  </TimelineCard>
</template>

<script setup lang="ts">
import TimelineCard from '../ActivityFeed/Base/TimelineCard.vue'
import TimelineTag from '../ActivityFeed/Base/TimelineTag.vue'
import TimelineFooter from '../ActivityFeed/Base/TimelineFooter.vue'
import type { Task, Business, Contact, UserWorkSpace, User } from '@prisma/client'

const { TimeStampFormat, formatDate, isPastDate } = useUtils()

const props = defineProps<{
  task: Task & {
    Business?: Business
    Contact?: Contact
    UserWorkSpace?: UserWorkSpace
  }
  users: User[]
}>()

const emit = defineEmits(['refresh'])

const isOverdue = computed(() => {
  const task = props.task

  if (task.isCompleted) {
    return false
  }

  if (task.overdue !== undefined) {
    return task.overdue
  }

  if (task.dueDate?.original) {
    return isPastDate(task.dueDate.original)
  }

  return false
})

const getStatusIcon = (isCompleted: boolean, overdue: boolean): string => {
  if (isCompleted) {
    return 'mdi:checkbox-marked-circle'
  }
  if (overdue) {
    return 'mdi:alert-circle-outline'
  }
  return 'mdi:checkbox-blank-circle-outline'
}

const getStatusLabel = (isCompleted: boolean, overdue: boolean): string => {
  if (isCompleted) {
    return 'Completed'
  }
  if (overdue) {
    return 'Overdue'
  }
  return 'Pending'
}

type VariantType = 'blue' | 'cyan' | 'purple' | 'yellow' | 'orange' | 'lime' | 'indigo' | 'green' | 'red' | 'emerald' | 'amber' | 'gray'

const getStatusVariant = (isCompleted: boolean, overdue: boolean): VariantType => {
  if (isCompleted) {
    return 'emerald' as const
  }
  if (overdue) {
    return 'red' as const
  }
  return 'amber' as const
}

const getDueDateVariant = (overdue: boolean, dueToday: boolean, isCompleted: boolean): VariantType => {
  if (isCompleted) {
    return 'emerald' as const
  }
  if (overdue) {
    return 'red' as const
  }
  if (dueToday) {
    return 'blue' as const
  }
  return 'gray' as const
}

const formatDueDate = (dueDate: { original: string, formatted: string, sorting: string }) => {
  return formatDate(dueDate.original, TimeStampFormat.DayMonthYearTime)
}

const toast = useToast()
const { updateTaskStatus } = useTasks()

const changeTaskStatus = async (id: string, status: boolean) => {
  try {
    const { error } = await updateTaskStatus(id, status)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update task status',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: status ? 'Task completed successfully' : 'Task reopened successfully',
      life: 3000,
    })

    emit('refresh')
  }
  catch (error) {
    console.error('Error updating task status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update task status',
      life: 3000,
    })
  }
}
</script>
